import React from "react";
import { useDispatch } from "react-redux";
import { SupportContact } from "../support-contact";
import { logoutAsync } from "../../state/ducks/auth";
import { accessCleared } from "../../state/ducks/access";
import { clearActivity } from "../../state/utils/activity";
import {
  onSessionEnded,
  resetSessionEnded,
  SessionEndReason,
} from "../../state/utils/session-ended";

/**
 * Which endings send the user onward by themselves. Everything else waits for
 * the button, exactly as before this existed.
 *
 * `displaced` is the one worth arguing about, and it is deliberately absent: it
 * is the only variant carrying a support phone number, and a dialog that erases
 * a phone number while somebody is dialling it is worse than one that waits. A
 * screen reader may also still be reading it out, and a tab that has been hidden
 * a while has its timers throttled — so the countdown can already be at zero by
 * the time the user looks, and the dialog vanishes on sight.
 *
 * Not exhaustiveness-checked on purpose: a new SessionEndReason defaults to "no
 * timer", which is the safe direction.
 */
const AUTO_REDIRECT_REASONS: ReadonlySet<SessionEndReason> =
  new Set<SessionEndReason>(["expired", "idle"]);

/**
 * `idleTimeoutMinutes` arrives as a prop rather than being read from
 * `timings()` here, because this must be evaluated at RENDER time. A
 * module-level constant would be evaluated at import time — before
 * ConfigProvider has called `setTimings()` — and would permanently quote
 * DEFAULT_TIMINGS instead of the operator's value. That is invisible while
 * config.json happens to also say 30.
 */
const copyFor = (
  reason: SessionEndReason,
  idleTimeoutMinutes: number,
): { title: string; message: string; securityNotice?: string } => {
  switch (reason) {
    case "displaced":
      return {
        title: "Signed out",
        message:
          "Your account was signed in on another device, so this session has ended.",
        // The ONLY reason that can indicate someone else using the account.
        // Expiry and inactivity are routine, and telling a user to report
        // those as suspicious would train them to ignore the warning that
        // matters.
        securityNotice: "If this was not you, please contact technical support.",
      };
    case "expired":
      return {
        title: "Session expired",
        message: "Your session has expired. Please sign in again.",
      };
    case "idle":
      return {
        title: "Signed out",
        message: `You were signed out because you were inactive for ${
          idleTimeoutMinutes === 1 ? "1 minute" : `${idleTimeoutMinutes} minutes`
        }.`,
      };
  }
};

interface SessionEndedDialogProps {
  /** From config.json, so the message matches the timeout actually in force. */
  idleTimeoutMinutes: number;
  /** How long the countdown runs, for the reasons that get one. */
  redirectMs: number;
}

interface Ended {
  reason: SessionEndReason;
  /** When the signal arrived — the countdown's anchor. */
  at: number;
}

/**
 * Terminal dialog: the session is over and the user is going back to the login
 * page. Blocking, because a redirect with no explanation reads as a bug and a
 * toast would be missed — the user must see WHY they were signed out before the
 * page changes under them.
 *
 * The redirect then happens on its own after a visible countdown, so an
 * unattended screen returns to the login page instead of sitting on a dead
 * session. The button only skips the wait; it is no longer the only way out.
 * Which endings get a countdown is AUTO_REDIRECT_REASONS above.
 *
 * Sibling of the routes inside <Router>, owning its own state — state above
 * <Router> would remount the whole Dashboard (see activity.ts).
 */
export const SessionEndedDialog: React.FC<SessionEndedDialogProps> = ({
  idleTimeoutMinutes,
  redirectMs,
}) => {
  // One state object, not two: the listener below fires from the heartbeat,
  // outside React 16's event batching, so separate setters would render twice.
  const [ended, setEnded] = React.useState<Ended | null>(null);
  const [remainingMs, setRemainingMs] = React.useState(redirectMs);
  const dispatch = useDispatch();

  // We leave exactly once, whether the countdown ran out or the user clicked.
  // Synchronous and never reset, because `window.location.href` does NOT
  // navigate synchronously: it queues a navigation and the document stays live
  // and interactive until the new response commits, so a queued tick or a
  // second click can still land. If a dismiss-without-redirect path is ever
  // added, it must reset this.
  const leftRef = React.useRef(false);

  React.useEffect(
    () =>
      onSessionEnded((reason) => {
        // A late signal — an in-flight 401 landing during that unload window —
        // would otherwise re-open the dialog on top of a navigation already
        // under way, with a countdown that can never fire again.
        if (leftRef.current) return;
        setEnded({ reason, at: Date.now() });
      }),
    [],
  );

  // Deliberately reads no state, which is what makes [dispatch] a complete
  // dependency list. If it ever needs `ended`, add it — the interval below
  // captures this closure and would otherwise go stale.
  const acknowledge = React.useCallback(() => {
    if (leftRef.current) return;
    leftRef.current = true;
    setEnded(null);
    resetSessionEnded();
    clearActivity();
    dispatch(accessCleared());
    dispatch(logoutAsync());
    // Full reload rather than a router push: every page's cached data belongs
    // to the signed-out user, and any in-flight request still holds the dead
    // token.
    window.location.href = "/";
  }, [dispatch]);

  const startedAt =
    ended && AUTO_REDIRECT_REASONS.has(ended.reason) ? ended.at : null;

  // Derived from an absolute deadline each tick rather than decremented, so
  // clock drift, background-tab throttling and a laptop sleeping through the
  // whole countdown cannot make it wrong. Crucially the SAME number drives the
  // display and the redirect, so the two can never disagree — a parallel
  // setTimeout could fire while the dialog still read "1s", or leave it reading
  // "0s" for a full minute.
  React.useEffect(() => {
    if (startedAt === null) return;
    const deadline = startedAt + redirectMs;
    const tick = () => {
      const left = Math.max(0, deadline - Date.now());
      // Before the setState, so "0s" is never rendered — and in the tick, never
      // in the render body, because this is a side effect and not a derivation.
      if (left === 0) {
        acknowledge();
        return;
      }
      setRemainingMs(left);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startedAt, redirectMs, acknowledge]);

  if (!ended) return null;

  const { title, message, securityNotice } = copyFor(
    ended.reason,
    idleTimeoutMinutes,
  );
  const counting = startedAt !== null;

  return (
    <div className="session-ended-backdrop" role="presentation">
      <div
        className="session-ended-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="session-ended-title"
        aria-describedby="session-ended-message"
      >
        <div className="session-ended-title" id="session-ended-title">
          {title}
        </div>
        <div
          className={`session-ended-message${securityNotice ? " tight" : ""}`}
          id="session-ended-message"
        >
          {message}
        </div>
        {securityNotice && (
          <div className="support-box">
            <div className="support-text">{securityNotice}</div>
            <SupportContact />
          </div>
        )}
        {counting && (
          <div className="session-ended-countdown">
            Returning to the login page in{" "}
            {/* aria-live off: a one-second region would be read on every tick.
                The alertdialog role announces the whole subtree on mount, and
                remainingMs starts at redirectMs, so the first paint already
                carries the full number. */}
            <span
              className="session-ended-countdown-value"
              role="timer"
              aria-live="off"
            >
              {Math.ceil(remainingMs / 1000)}s
            </span>
          </div>
        )}
        <button className="session-ended-button" onClick={acknowledge} autoFocus>
          {counting ? "SIGN IN NOW" : "OK"}
        </button>
      </div>
    </div>
  );
};
